import { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert } from 'react-bootstrap';
import './AdminEditor.css';

// Import JSON files directly (same as main component)
import XSeriesData from '../GenenralPhysics/data/XSeries.json';
import YSeriesData from '../GenenralPhysics/data/YSeries.json';
import QuantData from '../GenenralPhysics/data/Quant.json';
import ASeriesData from '../GenenralPhysics/data/ASeries.json';
import BSeriesData from '../GenenralPhysics/data/BSeries.json';
import CSeriesData from '../GenenralPhysics/data/CSeries.json';
import ClassicalElectrodynamicsData from '../GenenralPhysics/data/ClassicalElectrodynamics.json';
import ClassicalMechanicsData from '../GenenralPhysics/data/ClassicalMechanics.json';
import QuantXSeriesData from '../GenenralPhysics/data/QuantXSeries.json';
import QuantYSeriesData from '../GenenralPhysics/data/QuantYSeries.json';
import IntellectualBeautyHidingInPhysicsData from '../GenenralPhysics/data/IntellectualBeautyHidingInPhysics.json';
import LinksData from '../GenenralPhysics/data/Links.json';
import GalileoData from '../GenenralPhysics/data/GalileoToEinstein.json';
import MathematicalApproachesData from '../GenenralPhysics/data/MathematicalApproaches.json';

// Map filenames to imported data
const FILE_DATA_MAP = {
  'XSeries.json': XSeriesData,
  'YSeries.json': YSeriesData,
  'Quant.json': QuantData,
  'ASeries.json': ASeriesData,
  'BSeries.json': BSeriesData,
  'CSeries.json': CSeriesData,
  'ClassicalElectrodynamics.json': ClassicalElectrodynamicsData,
  'ClassicalMechanics.json': ClassicalMechanicsData,
  'QuantXSeries.json': QuantXSeriesData,
  'QuantYSeries.json': QuantYSeriesData,
  'IntellectualBeautyHidingInPhysics.json': IntellectualBeautyHidingInPhysicsData,
  'Links.json': LinksData,
  'GalileoToEinstein.json': GalileoData,
  'MathematicalApproaches.json': MathematicalApproachesData,
};

const DATA_FILES = [
  'ASeries.json',
  'BSeries.json',
  'CSeries.json',
  'ClassicalElectrodynamics.json',
  'ClassicalMechanics.json',
  'GalileoToEinstein.json',
  'IntellectualBeautyHidingInPhysics.json',
  'Links.json',
  'MathematicalApproaches.json',
  'Quant.json',
  'QuantXSeries.json',
  'QuantYSeries.json',
  'XSeries.json',
  'YSeries.json',
];

const AdminEditor = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [selectedFile, setSelectedFile] = useState('');
  const [fileData, setFileData] = useState(null);
  const [editedData, setEditedData] = useState(null);
  // Batch editing: track all files being edited
  const [editedFiles, setEditedFiles] = useState({}); // { filename: { original: data, edited: data } }
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draggedLink, setDraggedLink] = useState(null); // { itemIndex, linkIndex }

  // Check if user is already authenticated
  useEffect(() => {
    const authStatus = sessionStorage.getItem('admin_authenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setError('');
    setLoading(true);
    
    if (!password) {
      setError('Please enter a password');
      setLoading(false);
      return;
    }

    try {
      // Verify password server-side for security
      const response = await fetch('/api/admin/verify-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (data.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem('admin_authenticated', 'true');
        setPassword('');
      } else {
        setError(data.error || 'Invalid password');
      }
    } catch (err) {
      console.error('Auth error:', err);
      setError('Failed to verify password. Make sure API routes are available.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('admin_authenticated');
    setSelectedFile('');
    setFileData(null);
  };

  const updateBatchFile = (filename, editedData) => {
    if (!filename) return;
    setEditedFiles(prev => {
      // If file not in batch yet, initialize it
      if (!prev[filename]) {
        const originalData = FILE_DATA_MAP[filename];
        if (!originalData) return prev; // File doesn't exist
        return {
          ...prev,
          [filename]: {
            original: JSON.parse(JSON.stringify(originalData)),
            edited: editedData,
          }
        };
      }
      // Update existing file
      return {
        ...prev,
        [filename]: {
          ...prev[filename],
          edited: editedData,
        }
      };
    });
  };

  const hasUnsavedChanges = (filename) => {
    if (!editedFiles[filename]) return false;
    const original = JSON.stringify(editedFiles[filename].original);
    const edited = JSON.stringify(editedFiles[filename].edited);
    return original !== edited;
  };

  const getUnsavedFiles = () => {
    return Object.keys(editedFiles).filter(filename => hasUnsavedChanges(filename));
  };

  const loadFile = (filename) => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Load directly from imported JSON files (same source as main component)
      const fileData = FILE_DATA_MAP[filename];
      
      if (!fileData) {
        throw new Error(`File ${filename} not found in imports`);
      }

      const originalData = JSON.parse(JSON.stringify(fileData)); // Deep copy
      const editedDataCopy = editedFiles[filename]?.edited || originalData;

      setFileData(originalData);
      setEditedData(editedDataCopy);
      setSelectedFile(filename);

      // Update batch tracking
      if (!editedFiles[filename]) {
        setEditedFiles(prev => ({
          ...prev,
          [filename]: {
            original: originalData,
            edited: editedDataCopy,
          }
        }));
      }
    } catch (err) {
      console.error('Error loading file:', err);
      setError(`Failed to load file: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAll = async () => {
    const unsavedFiles = getUnsavedFiles();
    
    if (unsavedFiles.length === 0) {
      setSuccess('No changes to save');
      return;
    }

    setError('');
    setSuccess('');
    setSaving(true);

    try {
      // Validate all files first
      for (const filename of unsavedFiles) {
        const data = editedFiles[filename].edited;
        JSON.stringify(data); // Validate JSON
      }

      // Save all files in parallel
      const savePromises = unsavedFiles.map(async (filename) => {
        const data = editedFiles[filename].edited;
        const response = await fetch(`/api/admin/data/${filename}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Network error' }));
          throw new Error(`${filename}: ${errorData.error || `HTTP ${response.status}`}`);
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(`${filename}: ${result.error || 'Failed to save'}`);
        }

        return { filename, success: true };
      });

      const results = await Promise.allSettled(savePromises);
      
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected');

      if (failed.length > 0) {
        const errors = failed.map(r => r.reason?.message || 'Unknown error').join('; ');
        setError(`Some files failed to save: ${errors}`);
      }

      if (successful > 0) {
        // Update original data for successfully saved files
        const savedFiles = results
          .filter(r => r.status === 'fulfilled')
          .map(r => r.value.filename);

        setEditedFiles(prev => {
          const updated = { ...prev };
          savedFiles.forEach(filename => {
            if (updated[filename]) {
              updated[filename] = {
                original: updated[filename].edited,
                edited: updated[filename].edited,
              };
            }
          });
          return updated;
        });

        // Update current file if it was saved
        if (savedFiles.includes(selectedFile)) {
          setFileData(editedFiles[selectedFile].edited);
        }

        setSuccess(`Saved ${successful} file(s)! Changes committed to GitHub. Vercel will redeploy automatically (~1-2 minutes).`);
      }
    } catch (err) {
      console.error('Batch save error:', err);
      setError('Failed to save files: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    // Single file save - now just updates the batch and calls save all
    if (!selectedFile || !editedData) return;
    updateBatchFile(selectedFile, editedData);
    await handleSaveAll();
  };

  const handleAddItem = () => {
    if (!editedData || !Array.isArray(editedData) || !selectedFile) return;

    const newItem = {
      title: 'New Item',
      links: [
        { isYoutube: false, link: '', text: '' },
        { isYoutube: true, link: '', text: '' },
      ],
    };

    const newData = [...editedData, newItem];
    setEditedData(newData);
    updateBatchFile(selectedFile, newData);
  };

  const handleDeleteItem = (index) => {
    if (!editedData || !Array.isArray(editedData) || !selectedFile) return;
    if (window.confirm('Are you sure you want to delete this item?')) {
      const newData = editedData.filter((_, i) => i !== index);
      setEditedData(newData);
      updateBatchFile(selectedFile, newData);
    }
  };

  const handleUpdateItem = (index, field, value) => {
    if (!editedData || !Array.isArray(editedData) || !selectedFile) return;
    // Create immutable update: new array with new object
    const newData = editedData.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    );
    setEditedData(newData);
    // Update batch tracking so changes are saved
    updateBatchFile(selectedFile, newData);
  };

  const handleUpdateLink = (itemIndex, linkIndex, field, value) => {
    if (!editedData || !Array.isArray(editedData) || !selectedFile) return;
    // Create immutable update: new array, new item object, new links array, new link object
    const newData = editedData.map((item, i) => {
      if (i !== itemIndex) return item;
      return {
        ...item,
        links: item.links.map((link, j) => 
          j === linkIndex ? { ...link, [field]: value } : link
        )
      };
    });
    setEditedData(newData);
    // Update batch tracking so changes are saved
    updateBatchFile(selectedFile, newData);
  };

  const handleAddLink = (itemIndex) => {
    if (!editedData || !Array.isArray(editedData) || !selectedFile) return;
    // Create immutable update: new array, new item object, new links array
    const newData = editedData.map((item, i) => {
      if (i !== itemIndex) return item;
      return {
        ...item,
        links: [...item.links, { isYoutube: false, link: '', text: '' }]
      };
    });
    setEditedData(newData);
    updateBatchFile(selectedFile, newData);
  };

  const handleDeleteLink = (itemIndex, linkIndex) => {
    if (!editedData || !Array.isArray(editedData) || !selectedFile) return;
    // Create immutable update: new array, new item object, new links array
    const newData = editedData.map((item, i) => {
      if (i !== itemIndex) return item;
      return {
        ...item,
        links: item.links.filter((_, j) => j !== linkIndex)
      };
    });
    setEditedData(newData);
    updateBatchFile(selectedFile, newData);
  };

  const handleDragStart = (itemIndex, linkIndex) => {
    setDraggedLink({ itemIndex, linkIndex });
  };

  const handleDragOver = (e) => {
    e.preventDefault(); // Allow drop
    e.stopPropagation(); // Prevent event bubbling
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, itemIndex, dropLinkIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedLink || !editedData || !Array.isArray(editedData) || !selectedFile) {
      setDraggedLink(null);
      return;
    }
    
    const { itemIndex: dragItemIndex, linkIndex: dragLinkIndex } = draggedLink;
    
    // Only reorder if it's the same item and different positions
    if (itemIndex !== dragItemIndex || dragLinkIndex === dropLinkIndex) {
      setDraggedLink(null);
      return;
    }

    // Create immutable update: reorder links within the item
    const newData = editedData.map((item, i) => {
      if (i !== itemIndex) return item;
      
      const newLinks = [...item.links];
      const [draggedLinkItem] = newLinks.splice(dragLinkIndex, 1);
      newLinks.splice(dropLinkIndex, 0, draggedLinkItem);
      
      return {
        ...item,
        links: newLinks
      };
    });
    
    // Update both editedData and batch tracking to ensure changes are saved
    setEditedData(newData);
    updateBatchFile(selectedFile, newData);
    setDraggedLink(null);
  };

  const handleDragEnd = () => {
    setDraggedLink(null);
  };

  if (!isAuthenticated) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={6}>
            <Card>
              <Card.Header>
                <h3>Admin Access</h3>
              </Card.Header>
              <Card.Body>
                <Alert variant="info">
                  Enter the master password to access the admin panel.
                </Alert>
                <Form onSubmit={handleAuthSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Master Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Enter master password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoFocus
                    />
                  </Form.Group>
                  {error && <Alert variant="danger">{error}</Alert>}
                  <Button 
                    variant="primary" 
                    type="submit" 
                    className="w-100"
                  >
                    Authenticate
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2>Admin Data Editor</h2>
              {getUnsavedFiles().length > 0 && (
                <Alert variant="warning" className="mt-2 mb-0 py-1">
                  {getUnsavedFiles().length} file(s) with unsaved changes
                </Alert>
              )}
            </div>
            <div>
              {getUnsavedFiles().length > 0 && (
                <Button 
                  variant="success" 
                  className="me-2" 
                  onClick={handleSaveAll} 
                  disabled={saving}
                >
                  {saving ? `Saving ${getUnsavedFiles().length} file(s)...` : `Save All (${getUnsavedFiles().length})`}
                </Button>
              )}
              <Button variant="outline-danger" onClick={handleLogout}>
                Logout
              </Button>
            </div>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

          <Row>
            <Col md={3}>
              <Card>
                <Card.Header>Select File</Card.Header>
                <Card.Body>
                  <Form.Select
                    value={selectedFile}
                    onChange={(e) => {
                      if (e.target.value) {
                        loadFile(e.target.value);
                      } else {
                        setSelectedFile('');
                        setFileData(null);
                        setEditedData(null);
                      }
                    }}
                  >
                    <option value="">-- Select a file --</option>
                    {DATA_FILES.map((file) => {
                      const hasChanges = hasUnsavedChanges(file);
                      return (
                        <option key={file} value={file}>
                          {file} {hasChanges ? '●' : ''}
                        </option>
                      );
                    })}
                  </Form.Select>
                  {getUnsavedFiles().length > 0 && (
                    <div className="mt-2">
                      <small className="text-muted">
                        Files with changes: {getUnsavedFiles().join(', ')}
                      </small>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col md={9}>
              {editedData && Array.isArray(editedData) && (
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <span>Editing: {selectedFile}</span>
                    <div>
                      <Button variant="success" className="me-2" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : hasUnsavedChanges(selectedFile) ? 'Save This File' : 'No Changes'}
                      </Button>
                      <Button variant="secondary" onClick={() => {
                        const original = editedFiles[selectedFile]?.original || fileData;
                        setEditedData(JSON.parse(JSON.stringify(original)));
                        updateBatchFile(selectedFile, JSON.parse(JSON.stringify(original)));
                      }}>
                        Reset
                      </Button>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-3">
                      <Button variant="outline-primary" size="sm" onClick={handleAddItem}>
                        + Add New Item
                      </Button>
                    </div>

                    <div className="admin-editor-items">
                      {editedData.map((item, itemIndex) => (
                        <Card key={itemIndex} className="mb-3">
                          <Card.Header className="d-flex justify-content-between align-items-center">
                            <Form.Control
                              type="text"
                              value={item.title || ''}
                              onChange={(e) => handleUpdateItem(itemIndex, 'title', e.target.value)}
                              placeholder="Item Title"
                              className="me-2"
                            />
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteItem(itemIndex)}
                            >
                              Delete Item
                            </Button>
                          </Card.Header>
                          <Card.Body>
                            <h6>Links:</h6>
                            {item.links && item.links.map((link, linkIndex) => (
                              <div 
                                key={linkIndex} 
                                className="mb-2 p-2 border rounded"
                                draggable
                                onDragStart={(e) => {
                                  handleDragStart(itemIndex, linkIndex);
                                  e.dataTransfer.effectAllowed = 'move';
                                }}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDrop(e, itemIndex, linkIndex)}
                                onDragEnd={handleDragEnd}
                                style={{
                                  cursor: 'move',
                                  opacity: draggedLink?.itemIndex === itemIndex && draggedLink?.linkIndex === linkIndex ? 0.5 : 1,
                                  backgroundColor: draggedLink?.itemIndex === itemIndex && draggedLink?.linkIndex === linkIndex ? '#f0f0f0' : 'transparent'
                                }}
                              >
                                <Row className="align-items-center">
                                  <Col md={1} className="text-center">
                                    <span style={{ cursor: 'grab', fontSize: '1.2em' }}>⋮⋮</span>
                                  </Col>
                                  <Col md={1}>
                                    <Form.Check
                                      type="checkbox"
                                      label="YouTube"
                                      checked={link.isYoutube || false}
                                      onChange={(e) => handleUpdateLink(itemIndex, linkIndex, 'isYoutube', e.target.checked)}
                                    />
                                  </Col>
                                  <Col md={4}>
                                    <Form.Control
                                      type="text"
                                      placeholder="Link URL"
                                      value={link.link || ''}
                                      onChange={(e) => handleUpdateLink(itemIndex, linkIndex, 'link', e.target.value)}
                                    />
                                  </Col>
                                  <Col md={4}>
                                    <Form.Control
                                      type="text"
                                      placeholder="Link Text (optional)"
                                      value={link.text || ''}
                                      onChange={(e) => handleUpdateLink(itemIndex, linkIndex, 'text', e.target.value)}
                                    />
                                  </Col>
                                  <Col md={2}>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => handleDeleteLink(itemIndex, linkIndex)}
                                    >
                                      Remove
                                    </Button>
                                  </Col>
                                </Row>
                              </div>
                            ))}
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleAddLink(itemIndex)}
                            >
                              + Add Link
                            </Button>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              )}

              {editedData && !Array.isArray(editedData) && (
                <Card>
                  <Card.Header>Editing: {selectedFile}</Card.Header>
                  <Card.Body>
                    <Form.Group>
                      <Form.Label>JSON Data</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={20}
                        value={JSON.stringify(editedData, null, 2)}
                        onChange={(e) => {
                          try {
                            const parsed = JSON.parse(e.target.value);
                            setEditedData(parsed);
                            if (selectedFile) {
                              updateBatchFile(selectedFile, parsed);
                            }
                            setError('');
                          } catch (err) {
                            setError('Invalid JSON');
                          }
                        }}
                      />
                    </Form.Group>
                    <Button variant="success" className="mt-3" onClick={handleSave} disabled={loading}>
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminEditor;

